import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveProfileActiveUntil,
    selectActiveProfileDescription,
} from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { TelephoneNumber } from 'flow/types/TelephoneNumber';
import { getProfileLength } from 'flow/utils';
import { getStandardProfileDescriptionInUserLanguage } from 'flow/utils/profile-utils';
import useAppContext from 'flow/hooks/useAppContext';
import ContactSubtitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactSubtitle';

interface Props {
    contact?: TypedMap<ContactFull>;
    contactFullName: string;
    headerNumber?: TypedMap<TelephoneNumber>;
    colleagueKey: ExtensionKey | null;
}

/**
 * Second row of the contact summary card. For a colleague (a contact that maps
 * to an internal extension) it shows their current presence profile — localized
 * name + "until <time>" — otherwise it falls back to the shared contact
 * subtitle (number / company / country).
 */
const ContactCardSubtitle = ({
    contact,
    contactFullName,
    headerNumber,
    colleagueKey,
}: Props) => {
    const { i18next } = useAppContext();

    const profileDescription = useAppSelector((state) =>
        colleagueKey
            ? selectActiveProfileDescription(state, colleagueKey)
            : undefined,
    );
    const profileActiveUntil = useAppSelector((state) =>
        colleagueKey
            ? selectActiveProfileActiveUntil(state, colleagueKey)
            : undefined,
    );

    // Colleague presence row: localized profile name + "until <time>", reusing
    // the shared smart formatter (handles tomorrow / a date, not just HH:mm).
    const profileRow = profileDescription
        ? [
              getStandardProfileDescriptionInUserLanguage(
                  profileDescription,
                  i18next,
              ),
              profileActiveUntil &&
                  `${i18next.t('profilechooser.until')} ${getProfileLength(profileActiveUntil, i18next)}`,
          ]
              .filter(Boolean)
              .join(' ')
        : undefined;

    return (
        <span className="truncate text-sm leading-6">
            {colleagueKey && profileRow ? (
                profileRow
            ) : (
                <ContactSubtitle
                    contact={contact}
                    contactFullName={contactFullName}
                    number={headerNumber}
                />
            )}
        </span>
    );
};

export default React.memo(ContactCardSubtitle);
